import React, { Suspense, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import {
  HMSRoomProvider,
  selectIsConnectedToRoom,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Box, HMSThemeProvider } from "@100mslive/roomkit-react";
import { AppData } from "./components/AppData/AppData.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary";
import FullPageProgress from "./components/FullPageProgress";
import { Init } from "./components/init/Init";
import { KeyboardHandler } from "./components/Input/KeyboardInputManager";
import { Notifications } from "./components/Notifications";
import PostLeave from "./components/PostLeave";
import { ToastContainer } from "./components/Toast/ToastContainer";
import EventsLayout from "./layouts/EventsLayout";
import EventsLayoutFull from "./layouts/EventsLayoutFull";
import {
  SixAboutPage,
  SixBroadcastPage,
  SixCareersPage,
  SixDemoPage,
  SixDisclaimerPage,
  SixMeetingPage,
  SixPage,
  SixPrivacyPage,
  SixSecurityPage,
  SixTermsPage,
} from "./pages/6ix/6ixPage";
import SixPriceingPage from "./pages/6ix/SixPriceingPage";
import AccessTeamPage from "./pages/AccessTeamPage";
import AccountSettingPage from "./pages/AccountSettingPage";
import { AnalyticsInvitedPage, AnalyticsPage } from "./pages/AnalyticsPage";
import { AuthPage } from "./pages/AuthPage";
import CompanyPage from "./pages/CompanyPage";
import ConferencePage from "./pages/ConferencePage";
import ConstantContactPage from "./pages/ConstantContactPage";
import { DashboardPage } from "./pages/DashboardPage";
import { EventCreatePage, EventEditPage } from "./pages/EventPage";
import EventPreviewPage from "./pages/EventPreviewPage";
import { ExploreLandingPage, ExplorePage } from "./pages/ExplorePage";
import HubSpotPage from "./pages/HubSpotPage";
import { InvestorInvitedPage, InvestorReportPage } from "./pages/InvestorPage";
import HomePage from "./pages/Landing/HomePage.jsx";
import HomePage2 from "./pages/Landing/HomePage2.jsx";
import LandingPage from "./pages/Landing/LandingPage";
import { MailchimpCallBackPage } from "./pages/MailchimpCallBackPage";
import OnBoardingPage from "./pages/OnBoardingPage";
import { PasswordResetPage } from "./pages/PasswordResetPage.jsx";
import { ProfilePage } from "./pages/ProfilePage";
import RefreshPage from "./pages/RefreshPage";
import SupportPage from "./pages/SupportPage";
import { hmsActions, hmsNotifications, hmsStats, hmsStore } from "./hms.js";
import { Confetti } from "./plugins/confetti";
import { RemoteStopScreenshare } from "./plugins/RemoteStopScreenshare";
import { shadeColor } from "./common/utils";
import { FeatureFlags } from "./services/FeatureFlags";
import {
  getBackendEndpoint,
  getUserToken as defaultGetUserToken,
} from "./services/tokenService";

const defaultTokenEndpoint = process.env
  .REACT_APP_TOKEN_GENERATION_ENDPOINT_DOMAIN
  ? `${getBackendEndpoint()}${
      process.env.REACT_APP_TOKEN_GENERATION_ENDPOINT_DOMAIN
    }/`
  : process.env.REACT_APP_TOKEN_GENERATION_ENDPOINT;

const envPolicyConfig = JSON.parse(process.env.REACT_APP_POLICY_CONFIG || "{}");

let appName;
if (window.location.host.includes("localhost")) {
  appName = "localhost";
} else {
  appName = window.location.host.split(".")[0];
}

document.title =
  process.env.REACT_APP_TITLE || `${appName}'s ${document.title}`;

// TODO: remove now that there are options to change to portrait
const getAspectRatio = ({ width, height }) => {
  const host = process.env.REACT_APP_HOST_NAME || window.location.hostname;
  const portraitDomains = (
    process.env.REACT_APP_PORTRAIT_MODE_DOMAINS || ""
  ).split(",");
  if (portraitDomains.includes(host) && width > height) {
    return { width: height, height: width };
  }
  return { width, height };
};

export function EdtechComponent({
  roomId = "",
  tokenEndpoint = defaultTokenEndpoint,
  themeConfig: {
    aspectRatio = "1-1",
    font = "Roboto",
    color = "#E3C050",
    theme = "dark",
    logo = "",
    headerPresent = "false",
    metadata = "",
    recordingUrl = "",
  },
  getUserToken = defaultGetUserToken,
  policyConfig = envPolicyConfig,
  getDetails = () => {},
}) {
  const { 0: width, 1: height } = aspectRatio
    .split("-")
    .map(el => parseInt(el));

  const getUserTokenCallback = useCallback(getUserToken, []); //eslint-disable-line

  return (
    <ErrorBoundary>
      <HMSThemeProvider
        themeType={theme}
        aspectRatio={getAspectRatio({ width, height })}
        theme={{
          colors: {
            brandDefault: color,
            brandDark: shadeColor(color, -30),
            brandLight: shadeColor(color, 30),
            brandDisabled: shadeColor(color, 10),
          },
          fonts: {
            sans: [font, process.env.REACT_APP_FONT, "sans-serif"],
          },
        }}
      >
        <HMSRoomProvider
          isHMSStatsOn={FeatureFlags.enableStatsForNerds}
          actions={hmsActions}
          store={hmsStore}
          notifications={hmsNotifications}
          stats={hmsStats}
        >
          <AppData
            appDetails={metadata}
            policyConfig={policyConfig}
            recordingUrl={recordingUrl}
            logo={logo}
            tokenEndpoint={tokenEndpoint}
          />

          <Init />
          <Box
            className="events6ix-layout"
            css={{
              bg: "$mainBg",
              w: "100%",
              ...(headerPresent === "true"
                ? { flex: "1 1 0", minHeight: 0 }
                : { h: "100%" }),
            }}
          >
            <AppRoutes
              getUserToken={getUserTokenCallback}
              getDetails={getDetails}
            />
          </Box>
        </HMSRoomProvider>
      </HMSThemeProvider>
    </ErrorBoundary>
  );
}

const RouteList = ({ getUserToken, getDetails }) => {
  return (
    <Routes>
      <Route path="meeting">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <ConferencePage />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <ConferencePage />
            </Suspense>
          }
        />
      </Route>
      <Route path="leave">
        <Route path=":roomId/:role" element={<PostLeave />} />
        <Route path=":roomId" element={<PostLeave />} />
      </Route>

      <Route path="/mailchimp/callback" element={<MailchimpCallBackPage />} />
      <Route
        path="/constantcontact/callback"
        element={<ConstantContactPage />}
      />
      <Route path="/hubspot/callback" element={<HubSpotPage />} />
      <Route path="/access/team" element={<AccessTeamPage />} />
      <Route path="/auth" element={<AuthPage />} />

      <Route element={<EventsLayoutFull />}>
        <Route path="/on-boarding" element={<OnBoardingPage />} />
      </Route>

      <Route element={<EventsLayout />}>
        <Route path="/preview">
          <Route
            path=":roomId"
            element={
              <Suspense fallback={<FullPageProgress />}>
                <EventPreviewPage getUserToken={getUserToken} />
              </Suspense>
            }
          />
        </Route>
        <Route path="/event">
          <Route path="edit/:eventId" element={<EventEditPage />} />
          <Route path="analytics/:eventId" element={<AnalyticsPage />} />
          <Route
            path="analytics/invited/:eventId"
            element={<AnalyticsInvitedPage />}
          />
          <Route
            path=":roomId"
            element={
              <Suspense fallback={<FullPageProgress />}>
                <EventPreviewPage getUserToken={getUserToken} />
              </Suspense>
            }
          />
        </Route>

        <Route path="/account">
          <Route
            path="setting?/:settingType"
            element={<AccountSettingPage />}
          />
        </Route>

        <Route path="/investors">
          <Route path="invited" element={<InvestorInvitedPage />} />
          <Route path="" element={<InvestorReportPage />} />
        </Route>
        <Route path="password">
          <Route path="reset/:actionCode" element={<PasswordResetPage />} />
        </Route>

        <Route path="/create" element={<EventCreatePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/dashboard/:listType?" element={<DashboardPage />} />

        <Route
          path="/company/:orgSlug/:tabType?/:eventList?/:listType?"
          element={<CompanyPage />}
        />
        <Route path="/explore" element={<ExploreLandingPage />} />
        <Route path="/explore/list/:listType?" element={<ExplorePage />} />
        <Route path="/overview" element={<LandingPage />} />
        <Route path="/support" element={<SupportPage />} />

        <Route path="/pricing" element={<SixPriceingPage />} />
        <Route path="/demo" element={<SixDemoPage />} />
        <Route path="/about" element={<SixAboutPage />} />
        <Route path="/broadcast" element={<SixBroadcastPage />} />
        <Route path="/careers" element={<SixCareersPage />} />
        <Route path="/privacy-policy" element={<SixPrivacyPage />} />
        <Route path="/terms" element={<SixTermsPage />} />
        <Route path="/disclaimer" element={<SixDisclaimerPage />} />
        <Route path="/security" element={<SixSecurityPage />} />
        <Route path="/request-meeting" element={<SixMeetingPage />} />
        <Route path="/6ix" element={<SixPage />} />
        <Route path="/claim-your-page" element={<HomePage2 />} />
        <Route path="/R_C_P" element={<RefreshPage />} />
        <Route path="*" element={<HomePage />} />
      </Route>
    </Routes>
  );
};

const BackSwipe = () => {
  const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
  const hmsActions = useHMSActions();
  useEffect(() => {
    const onRouteLeave = async () => {
      if (isConnectedToRoom) {
        await hmsActions.leave();
      }
    };
    window.addEventListener("popstate", onRouteLeave);
    return () => {
      window.removeEventListener("popstate", onRouteLeave);
    };
  }, [hmsActions, isConnectedToRoom]);
  return null;
};

function AppRoutes({ getUserToken, getDetails }) {
  return (
    <Router>
      <ToastContainer />
      <Notifications />
      <BackSwipe />
      <Confetti />
      <RemoteStopScreenshare />
      <KeyboardHandler />
      <Routes>
        <Route
          path="/*"
          element={
            <RouteList getUserToken={getUserToken} getDetails={getDetails} />
          }
        />
        <Route
          path="/streaming/*"
          element={
            <RouteList getUserToken={getUserToken} getDetails={getDetails} />
          }
        />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <EdtechComponent
      themeConfig={{
        aspectRatio: process.env.REACT_APP_TILE_SHAPE,
        theme: process.env.REACT_APP_THEME,
        color: process.env.REACT_APP_COLOR,
        logo: process.env.REACT_APP_LOGO,
        font: process.env.REACT_APP_FONT,
        headerPresent: process.env.REACT_APP_HEADER_PRESENT,
        metadata: process.env.REACT_APP_DEFAULT_APP_DETAILS, // A stringified object in env
      }}
      getUserToken={defaultGetUserToken}
    />
  );
}
